// 11 services — bringing international brands into the Turkish market.
const services = [
  {
    slug: "market-entry-strategy",
    title: "Türkiye Market Entry Strategy",
    lead: "From a brand briefing in London or Munich to a defensible commercial position in Türkiye — written, costed and sequenced.",
    icon: "map",
    body: [
      "Most international brands enter Türkiye with confidence built on assumptions that do not hold once the campaign actually runs. Distribution structure, channel margins, consumer media habits and the regulatory and tax stack are all materially different from what global playbooks anticipate. Vitalis writes the entry plan that survives contact with the local market.",
      "Each engagement begins with a structured audit: category dynamics in Türkiye, distribution maps, price-pack architecture, comparable launches in the last five years, channel margins, competitive media weights and the realistic CAC each segment will accept. We then narrow to one or two opening propositions and design a phased entry plan with a real budget and a written risk register.",
      "The output is not a deck. It is a decision document — numbers your global CFO can validate, sequencing your CMO can defend, and timing the local commercial team can actually execute against."
    ],
    deliverables: [
      "Category, channel and price-pack audit across modern, traditional and online trade",
      "Sized opportunity with three-year revenue, CAC and contribution model",
      "Competitor positioning, share-of-voice and creative landscape audit",
      "Phased entry plan with budget, milestones and risk register",
      "Workshop with your global and regional leadership before approval"
    ]
  },
  {
    slug: "brand-localisation",
    title: "Brand Localisation & Cultural Adaptation",
    lead: "Türkçe naming, claims, tone of voice and creative — translated as ideas, not as words.",
    icon: "compass",
    body: [
      "Translation is the easiest part. What kills international brands in Türkiye is everything else: claims that test poorly with Turkish consumers, mood and tone that read as imported, photography that does not reflect the country, and packaging copy that misses the regulatory wording for the category.",
      "We localise as a creative discipline. Brand names tested with Turkish consumers. Claims reworked for the way the category is actually understood in TR. Photography re-shot or re-cast where the original assets feel out of place. Packaging, label and on-pack content rewritten for Turkish regulatory and retail context. Tone of voice guidelines that your in-house team and your agencies can apply consistently.",
      "The result is a Turkish expression of the brand — recognisable to global stakeholders, but unmistakably built for the Turkish consumer."
    ],
    deliverables: [
      "Türkçe naming, claims and tagline development with consumer testing",
      "Tone of voice and editorial guidelines (TR + EN)",
      "Local creative direction for photography, film and key visuals",
      "Packaging, label and on-pack copy aligned with TR regulation",
      "Brand book addendum for the Turkish market"
    ]
  },
  {
    slug: "performance-marketing",
    title: "Performance Marketing",
    lead: "Meta, Google, TikTok, programmatic — operated as a media business, not as a button-pushing exercise.",
    icon: "chart",
    body: [
      "Performance media in Türkiye looks superficially like performance media anywhere else. In practice, audience signal quality, creative density expectations, attribution windows and channel mix are all different — and most global playbooks underperform unless they are rebuilt locally.",
      "Vitalis runs paid media as a managed practice across Meta, Google, TikTok, programmatic display, retail media (Trendyol Ads, Hepsiburada Ads, Amazon.tr) and connected TV. Creative is produced in volume, in Turkish, with a velocity that matches what the platforms actually reward. Measurement is set up against incrementality, not last-click, with periodic media-mix and lift testing.",
      "We report monthly on contribution, CAC, ROAS and the metrics that map to your business — not the surface metrics that flatter agencies."
    ],
    deliverables: [
      "Channel-mix planning with sized investment by platform",
      "Creative production in volume, localised for Turkish audiences",
      "Campaign build, bidding, audience and exclusion management",
      "Incrementality, lift and media-mix testing on a planned cadence",
      "Monthly business-focused reporting in TR and EN"
    ]
  },
  {
    slug: "seo-organic",
    title: "SEO & Organic Search",
    lead: "Search behaviour in Türkçe is its own discipline — we build the content and technical foundation that wins it.",
    icon: "search",
    body: [
      "Turkish-language search has its own keyword landscape, its own SERP layout, its own dominant publishers and its own ranking dynamics. Importing an English SEO programme into Türkiye is one of the most common, most expensive mistakes international brands make in their first year.",
      "We run organic search as an end-to-end programme: Turkish keyword research, intent mapping, technical SEO audits, on-page optimisation, structured data, content production in volume by Turkish editors, and authority building through credible Turkish publications. Where appropriate we coordinate with the local press and influencer programmes so earned coverage compounds the SEO programme rather than running parallel to it.",
      "Reporting focuses on category share-of-voice, share of branded vs non-branded traffic, and revenue contribution — not vanity rank reports."
    ],
    deliverables: [
      "Türkçe keyword research, intent mapping and content roadmap",
      "Technical SEO audit and remediation programme",
      "Content production by Turkish editors with topical authority focus",
      "Structured data, hreflang, international SEO and Core Web Vitals",
      "Quarterly SOV reporting against named Turkish competitors"
    ]
  },
  {
    slug: "social-content",
    title: "Social Media & Content",
    lead: "Daily-operated Instagram, TikTok, YouTube, X and LinkedIn — staffed, on-tone, and on-brand.",
    icon: "messages",
    body: [
      "Turkish social audiences are large, active and unforgiving of brands that show up part-time. Posting cadences that pass elsewhere read as absent here, and a single mishandled comment thread can travel further than a quarter of advertising spend.",
      "Vitalis operates social channels as a managed service: strategy, monthly editorial planning, content production (still, motion and short-form video), publishing across all relevant platforms, community management in Türkçe with response SLAs, and crisis-response protocols rehearsed before they are needed.",
      "Channels are tuned to the audiences that actually matter for the brand — Instagram and TikTok for FMCG and lifestyle, LinkedIn for B2B and SaaS, YouTube for long-form, X for cultural moments and customer service."
    ],
    deliverables: [
      "Channel strategy and monthly editorial planning",
      "Content production: photography, short-form video, motion design",
      "Publishing, paid amplification and platform-native optimisation",
      "Community management in Türkçe with response SLAs",
      "Crisis-response protocols and rehearsed escalation paths"
    ]
  },
  {
    slug: "influencer-creator",
    title: "Influencer & Creator Partnerships",
    lead: "Curated, contracted and measured partnerships with Turkish creators — from mega-stars to micro communities.",
    icon: "users",
    body: [
      "Türkiye has one of the most active creator economies in Europe, and a creator strategy is no longer optional for consumer launches. The risk is not whether to work with creators; the risk is how to do it without burning budget on the wrong tier of partner.",
      "We identify, vet, contract and measure creator partnerships across Instagram, TikTok, YouTube, X and increasingly Twitch. Selection is grounded in audience-quality data, not follower counts. Contracts are negotiated with appropriate usage rights, exclusivity windows and content approval rights. Measurement is set up against incremental contribution to the brand's business — not unique-views vanity.",
      "Engagements range from one-off seeding campaigns to long-term ambassadorships and creator-led product collaborations."
    ],
    deliverables: [
      "Creator long-listing with audience-quality and brand-fit analysis",
      "Negotiation, contracting and usage-rights management",
      "Brief development and creative collaboration with the creator",
      "Campaign measurement against incremental business contribution",
      "Long-term ambassador and product-collaboration programmes"
    ]
  },
  {
    slug: "ecommerce-activation",
    title: "E-commerce & Marketplace Activation",
    lead: "Trendyol, Hepsiburada, Amazon.tr, n11 and your DTC store — operated as one commercial system.",
    icon: "cart",
    body: [
      "Türkiye is one of the world's most marketplace-heavy e-commerce markets. Trendyol, Hepsiburada, n11 and Amazon.tr together represent a category-defining share of online demand — and operating on them well requires its own playbook.",
      "Vitalis manages marketplace activation end-to-end: store setup and category positioning, price-pack architecture for online, A+ content and rich media, marketplace media (Trendyol Ads, Hepsiburada Ads), promotional planning aligned with each platform's commercial calendar, and performance against return-rate, attach-rate and contribution.",
      "Alongside marketplaces we run your own DTC store: localisation, payment methods that Turkish consumers actually use (BKM Express, installments, mobile wallets), logistics partnerships and on-site CRO to lift conversion in the local context."
    ],
    deliverables: [
      "Marketplace store setup, category and search optimisation",
      "A+ content, rich media and platform-native creative",
      "Marketplace media management across Trendyol Ads, Hepsiburada Ads, Amazon.tr",
      "DTC store localisation, payments and logistics partnerships",
      "Weekly trading reporting against ROI and contribution"
    ]
  },
  {
    slug: "pr-earned-media",
    title: "PR & Earned Media",
    lead: "Turkish press, trade publications and lifestyle titles — placed by people who already know the editors.",
    icon: "newspaper",
    body: [
      "Turkish media is a relationship business. The titles that move audiences for your category — Vogue Türkiye, GQ Türkiye, ELLE, Esquire, Capital, Forbes Türkiye, Sözcü, Hürriyet, sector trade press — respond to journalists they have worked with for years, not to brand pitches landing cold.",
      "Vitalis runs PR as a managed practice with senior journalists, former editors and trained publicists on the team. Campaigns are planned from product launches and corporate moments through to ongoing thought-leadership programmes that keep your brand in the right conversations. Crisis communications, when needed, is rehearsed and ready.",
      "We measure earned media by quality and contribution to brand search — not by clipping volumes."
    ],
    deliverables: [
      "Annual PR strategy with messaging architecture in TR and EN",
      "Press release writing, distribution and journalist outreach",
      "Press events, product launches and editor relations programmes",
      "Thought-leadership ghostwriting and bylined contributions",
      "Crisis communications planning and on-call response"
    ]
  },
  {
    slug: "retail-distribution",
    title: "Retail & Distribution Activation",
    lead: "Modern trade, specialty retail and distributor partnerships — staged so the shelf is ready when the campaign is.",
    icon: "shop",
    body: [
      "Air-cover marketing without distribution is wasted marketing. Vitalis works alongside your distributor or commercial partner to make sure the product is actually available, well-merchandised and supported by the right in-store and platform activation when the consumer turns up.",
      "We coordinate listing negotiations with the modern-trade chains relevant to your category, design and execute in-store programmes, manage trade-marketing campaigns, structure promotional calendars against retailer commercial cycles, and align field-marketing teams with the broader campaign.",
      "Where appropriate, we help you select and onboard distributor or importer partners — and structure the commercial terms that protect both sides through year one."
    ],
    deliverables: [
      "Distributor and importer selection where one is not already in place",
      "Modern-trade listing strategy, JBPs and category-management support",
      "In-store activation: POSM, sampling, demonstration programmes",
      "Trade-marketing calendars aligned with retailer commercial cycles",
      "Field-marketing and merchandising programme oversight"
    ]
  },
  {
    slug: "local-operations",
    title: "Local Customer Care & Operations",
    lead: "Türkçe customer service, returns and operations — protecting the brand the moment the consumer interacts.",
    icon: "support",
    body: [
      "Whatever the channel, the moment a Turkish consumer interacts with your brand they expect a Türkçe response, fast, with the cultural awareness of someone who has not been trained yesterday. Few launch teams are set up to deliver that — and the brand pays for it in reviews and refund rates.",
      "Vitalis operates Turkish-language customer service, social moderation, complaints handling, returns and refunds workflows, and post-purchase communication on your behalf. We work alongside whichever logistics, payments and platform partners are already in place, plugging into your CRM and ticketing systems rather than replacing them.",
      "SLAs are written, reported on monthly, and tuned to the category — luxury skincare expects different response speeds than B2B SaaS, and we calibrate accordingly."
    ],
    deliverables: [
      "Türkçe customer service across e-mail, chat, phone and social",
      "Returns, refunds and complaints workflow design and execution",
      "Social moderation, review response and rating management",
      "Post-purchase CRM and lifecycle messaging in Türkçe",
      "Monthly SLA reporting and continuous improvement programme"
    ]
  },
  {
    slug: "analytics-attribution",
    title: "Analytics, Attribution & Reporting",
    lead: "GA4, server-side tagging, MMM and incrementality — set up so you can trust the numbers you're reporting upstream.",
    icon: "graph",
    body: [
      "Most international brands launching in Türkiye discover, late, that their reporting stack is a story of best-effort estimates rather than reliable measurement. Server-side tracking is partial, consent is misconfigured, KPI definitions drift between platforms, and the local team ends up defending numbers no one fully trusts.",
      "Vitalis sets up the analytics and attribution layer the same way we set up the rest of the operation: properly, once, end-to-end. GA4 implemented and audited. Server-side tagging via GTM. Consent management built for KVKK and GDPR. Channel and platform reporting reconciled against a single source of truth. Where the business case justifies it, we run media-mix modelling and ongoing incrementality testing so leadership can make resource decisions on real evidence.",
      "Dashboards are kept boring on purpose. The point is decisions, not pixels."
    ],
    deliverables: [
      "GA4, GTM and server-side tagging build, audit and remediation",
      "KVKK + GDPR-aligned consent management and data governance",
      "Single source of truth dashboards for marketing, sales and finance",
      "Media-mix modelling and quarterly incrementality testing",
      "Monthly business review pack in TR and EN"
    ]
  }
];

export default services;
export const findService = (slug) => services.find((s) => s.slug === slug) || null;
